import React from 'react';
import { ArrowRight, ArrowLeft, CreditCard, Building2, CheckCircle, Gift, XCircle } from 'lucide-react';
import { Button } from '../ui/button';

const ExtrasPriceSummary = ({ 
  tripAmount, 
  extrasAmount, 
  selectedVehicle, 
  selectedExtras,
  autoIncludedExtras = [],
  onBack,
  onContinue,
  canContinue = true,
  isProcessing = false,
}) => {
  const totalAmount = tripAmount + extrasAmount;

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="extras-price-summary">
      {/* Orange top accent */}
      <div className="h-1 bg-[#FF8C00]" />

      <div className="p-6">
        <h3 className="font-bold text-black text-lg mb-4">Price Summary</h3>

        {/* Price Breakdown */}
        <div className="space-y-3 mb-6">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Trip</span>
            <span className="text-sm font-medium text-black">€{tripAmount}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Extras</span>
            <span className={`text-sm font-medium ${extrasAmount > 0 ? 'text-[#FF8C00]' : 'text-gray-400'}`}>
              {extrasAmount > 0 ? `€${extrasAmount}` : '€0'}
            </span>
          </div>
          <div className="border-t border-gray-100 pt-3">
            <div className="flex justify-between items-center">
              <span className="text-base font-semibold text-black">Total</span>
              <span className="text-2xl font-bold text-black font-display">
                €{totalAmount}
              </span>
            </div>
          </div>
        </div>

        {/* Selected Vehicle */}
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Selected Vehicle</p>
          <p className="text-sm font-semibold text-black">{selectedVehicle?.name || '—'}</p>
        </div>

        {/* Included Services (Auto-included / Gifts) */}
        {autoIncludedExtras.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-1.5 mb-2">
              <Gift className="h-3.5 w-3.5 text-green-600" />
              <p className="text-xs text-green-700 uppercase tracking-wider font-semibold">Included Free</p>
            </div>
            <ul className="space-y-1">
              {autoIncludedExtras.map((extra) => (
                <li key={extra.id} className="text-sm text-green-700 flex justify-between items-center">
                  <span>{extra.name}</span>
                  <span className="flex items-center gap-1">
                    <span className="text-gray-400 line-through text-xs">€{extra.originalPrice || 15}</span>
                    <span className="font-semibold text-green-600">FREE</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Selected Optional Extras */}
        <div className="bg-gray-50 rounded-lg p-3 mb-6">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Optional Extras</p>
          {selectedExtras.length > 0 ? (
            <ul className="space-y-1">
              {selectedExtras.map((extra) => (
                <li key={extra.id} className="text-sm text-black flex justify-between">
                  <span>{extra.name} {extra.quantity > 1 && `(×${extra.quantity})`}</span>
                  <span className="text-[#FF8C00]">
                    €{(extra.price || extra.defaultPrice) * (extra.quantity || 1)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-400">None selected</p>
          )}
        </div>

        {/* Buttons */}
        <div className="space-y-3">
          <Button
            data-testid="extras-continue-btn"
            className="w-full bg-[#FF8C00] text-white hover:bg-[#E67E00] h-12 text-base font-semibold transition-all duration-200 active:scale-[0.98]"
            onClick={onContinue}
            disabled={!canContinue || isProcessing}
          >
            {isProcessing ? 'Saving...' : 'Continue'}
            {!isProcessing && <ArrowRight className="h-5 w-5 ml-2" />}
          </Button>
          
          <Button
            data-testid="extras-back-btn"
            variant="outline"
            className="w-full border-gray-300 text-gray-700 hover:bg-gray-100 hover:text-gray-900 h-10"
            onClick={onBack}
            disabled={isProcessing}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>

        {/* Trust Strip with Free Cancellation */}
        <div className="mt-6 pt-4 border-t border-gray-100">
          {/* Free Cancellation Banner */}
          <div className="flex items-center justify-center gap-2 text-green-600 bg-green-50 rounded-lg py-2 mb-3">
            <XCircle className="h-4 w-4" />
            <span className="text-xs font-semibold">Free Cancellation (24h)</span>
          </div>
          
          <div className="grid grid-cols-3 gap-2">
            <div className="flex flex-col items-center text-center">
              <CreditCard className="h-4 w-4 text-[#FF8C00] mb-1" />
              <span className="text-[10px] text-gray-500">PayPal</span>
            </div>
            <div className="flex flex-col items-center text-center">
              <Building2 className="h-4 w-4 text-[#FF8C00] mb-1" />
              <span className="text-[10px] text-gray-500">UK-based</span>
            </div>
            <div className="flex flex-col items-center text-center">
              <CheckCircle className="h-4 w-4 text-[#FF8C00] mb-1" />
              <span className="text-[10px] text-gray-500">No hidden fees</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExtrasPriceSummary;
